/* TWPC Mobile Menu — Concept 01 "The Pantry Index"
 * Intercepts Heritage's hamburger below 750px and opens this menu instead.
 * Uses a native <dialog>, so focus trapping and Escape come from the browser.
 */
if (!customElements.get('twpc-menu')) {
  class TwpcMenu extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.focusTarget = this.querySelector('[data-twpc-focus]');
      this.mq = window.matchMedia('(max-width: 749px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.selector = this.dataset.trigger;

      this.onTrigger = this.onTrigger.bind(this);
      document.addEventListener('click', this.onTrigger, true);

      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.keyboard = true;
        this.close();
      });

      this.dialog.addEventListener('click', (e) => {
        if (e.target.closest('[data-twpc-close]')) {
          this.keyboard = e.detail === 0;
          this.close();
        } else if (e.target.closest('a[href]')) {
          this.close({ instant: true, restoreFocus: false });
        }
      });

      this.mq.addEventListener('change', (e) => {
        if (!e.matches && this.dialog.open) this.close({ instant: true, restoreFocus: false });
      });

      if (!document.querySelector(this.selector)) {
        console.warn('[twpc-menu] No hamburger button matched:', this.selector);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger, true);
    }

    onTrigger(e) {
      if (!this.mq.matches) return;
      const trigger = e.target.closest(this.selector);
      if (!trigger || this.contains(trigger)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.trigger = trigger;
      // A click with detail 0 came from the keyboard (Enter/Space), not a tap.
      this.keyboard = e.detail === 0;
      this.open();
    }

    resetHeritage() {
      // Make sure Heritage's own drawer stays closed and its icon stays a hamburger.
      if (!this.trigger) return;
      const details = this.trigger.closest('details');
      if (details) details.removeAttribute('open');
      this.trigger.blur();
    }

    open() {
      if (this.dialog.open) return;
      this.resetHeritage();
      document.documentElement.classList.add('twpc-menu-open');
      this.dialog.classList.toggle('is-keyboard', this.keyboard);
      this.dialog.showModal();
      // Taps focus the container (no ring); keyboard users land on the first link.
      if (!this.keyboard && this.focusTarget) this.focusTarget.focus({ preventScroll: true });
      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      this.refreshCart();
    }

    close({ instant = false, restoreFocus = true } = {}) {
      if (!this.dialog.open) return;
      this.dialog.classList.remove('is-open');
      const finish = () => {
        this.dialog.close();
        document.documentElement.classList.remove('twpc-menu-open');
        this.resetHeritage();
        // Only hand focus back to the hamburger for keyboard users.
        if (restoreFocus && this.keyboard && this.trigger) this.trigger.focus();
      };
      if (instant || this.reduced.matches) finish();
      else setTimeout(finish, 250);
    }

    async refreshCart() {
      const el = this.querySelector('[data-twpc-cart-count]');
      if (!el) return;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        el.textContent = cart.item_count;
      } catch (err) {
        /* keep the server-rendered count */
      }
    }
  }

  customElements.define('twpc-menu', TwpcMenu);
}
